import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import LanguageSwitcher from '../LanguageSwitcher';
import { APP_LOGO_SRC } from '../../constants/appLogo';
import './LoginPage.css';

const LoginPage = ({ onSuccess }) => {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [badgeId, setBadgeId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(t('pleaseEmailAndPassword'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Attempting username login');
      
      const userData = await authService.login(email, password);
      
      console.log('Login successful:', userData);
      
      if (onSuccess) {
        onSuccess(userData);
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      let errorMessage = t('loginFailed');
      const code = error?.code || '';
      const msg = error?.message || '';
      const isProduction = typeof window !== 'undefined' && window.location?.hostname && !window.location.hostname.match(/^localhost$/i);

      if (msg.includes('not active')) {
        errorMessage = t('accountNotActive');
      } else if (code.includes('unauthorized-domain') || msg.includes('unauthorized-domain')) {
        errorMessage = t('unauthorizedDomain');
      } else if (isProduction && code.includes('invalid-credential')) {
        // invalid-credential = wrong email/password OR domain/API key/reCAPTCHA block; show message that covers both
        errorMessage = t('invalidCredentialProduction');
      } else if (msg.includes('user-not-found') || msg.includes('wrong-password') || code.includes('wrong-password') || code.includes('user-not-found')) {
        errorMessage = t('invalidCredentials');
      } else if (msg.includes('too-many-requests')) {
        errorMessage = t('tooManyAttempts');
      } else if (msg) {
        errorMessage = msg;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeLogin = async (e) => {
    e.preventDefault();
    
    if (!badgeId || !pin) {
      setError(t('pleaseBadgeAndPin'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Attempting badge login');
      
      const userData = await authService.loginWithBadge(badgeId, pin);
      
      // Check if user has management/administration role
      if (userData.role_id !== 'management' && userData.role_id !== 'administration') {
        await authService.logout();
        setError(t('accessDeniedBadge'));
        return;
      }
      
      console.log('Badge login successful:', userData);
      
      if (onSuccess) {
        onSuccess(userData);
      }
    } catch (error) {
      console.error('Badge login failed:', error);
      
      let errorMessage = t('badgeLoginFailed');
      
      if (error.message.includes('not active')) {
        errorMessage = t('accountNotActive');
      } else if (error.message.includes('not-found') || error.message.includes('invalid-argument')) {
        errorMessage = t('invalidBadgeOrPin');
      } else if (error.message.includes('too-many-requests')) {
        errorMessage = t('tooManyAttempts');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setResetMessage(t('pleaseEnterEmail'));
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      console.log('Sending password reset for:', resetEmail);
      // Call Firebase Auth directly; no Firestore read (unauthenticated users cannot list users).
      // Auth sends email only if the account exists; does not leak existence.
      await authService.resetPassword(resetEmail);
      setResetMessage(t('resetLinkSent'));
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error('Password reset failed:', error);
      let errorMessage = t('resetFailed');
      if (error.message.includes('user-not-found')) {
        errorMessage = t('noUserFoundEmail');
      } else if (error.message.includes('invalid-email')) {
        errorMessage = t('invalidEmail');
      } else if (error.message.includes('too-many-requests')) {
        errorMessage = t('tooManyResetAttempts');
      } else if (error.message.includes('Missing or insufficient permissions')) {
        errorMessage = t('resetFailed');
      } else if (error.message) {
        errorMessage = error.message;
      }
      setResetMessage(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-language-switcher">
          <LanguageSwitcher />
        </div>
        {/* Logo */}
        <div className="logo-section">
          <img 
            src={APP_LOGO_SRC} 
            alt="ZeroPath" 
            className="login-logo"
          />
        </div>

        {error && (
          <div className="login-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Username and Password Fields */}
        <form onSubmit={handleUsernameLogin} className="username-form">
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('email')}
              disabled={loading}
              aria-required="true"
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('password')}
              disabled={loading}
              aria-required="true"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? t('loggingIn') : t('login')}
          </button>
          
          <div className="forgot-password-section">
            <button 
              type="button"
              className="forgot-password-link"
              onClick={() => setShowForgotPassword(true)}
              disabled={loading}
            >
              {t('forgotPassword')}
            </button>
          </div>
        </form>

        {/* Badge Authenticator */}
        <div className="badge-section">
          <div className="badge-divider">
            <span>{t('or')}</span>
          </div>
          
          <div className="badge-icon-container" onClick={handleBadgeLogin}>
            <div className="badge-icon">
              <svg width="240" height="160" viewBox="0 0 120 80" fill="none">
                <circle cx="60" cy="40" r="6" fill="url(#purpleGradient)"/>
                <path d="M60 40 L60 40" stroke="url(#purpleGradient)" strokeWidth="3" strokeLinecap="round"/>
                <path d="M50 40 A10 10 0 0 1 70 40" stroke="url(#purpleGradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
                <path d="M40 40 A20 20 0 0 1 80 40" stroke="url(#purpleGradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
                <path d="M30 40 A30 30 0 0 1 90 40" stroke="url(#purpleGradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
                <defs>
                  <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea"/>
                    <stop offset="100%" stopColor="#764ba2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <p className="badge-text">{t('touchBadge')}</p>
          </div>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="forgot-password-modal">
            <div className="modal-content">
              <h3>{t('resetPassword')}</h3>
              <p>{t('resetPasswordInstructions')}</p>
              
              {resetMessage && (
                <div className={`reset-message ${resetMessage.includes('Failed') || resetMessage.includes('eșuat') ? 'error' : 'success'}`}>
                  {resetMessage}
                </div>
              )}
              
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder={t('enterEmailPlaceholder')}
                    disabled={resetLoading}
                    aria-required="true"
                  />
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="submit" 
                    className="reset-button"
                    disabled={resetLoading}
                  >
                    {resetLoading ? t('sending') : t('sendResetLink')}
                  </button>
                  
                  <button 
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetMessage('');
                    }}
                    disabled={resetLoading}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Support and Help Text */}
        <div className="support-section">
          <p className="help-text">{t('needHelp')}</p>
          <p className="security-notice">{t('secureNotice')}</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;